import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, ListChecks, ImagePlus, Sparkles, PackagePlus, Star, TrendingUp, ShoppingBag, Users, BarChart3 } from 'lucide-react';

const STEPS = [
  {
    num: "1",
    icon: UserPlus,
    title: "Sign Up",
    color: "from-pink-500 to-rose-500",
    text: "Tap 'Continue with Google' or 'Continue with Phone'. Set up your profile with a photo, username, and bio. That's it — you're in."
  },
  {
    num: "2",
    icon: ListChecks,
    title: "Create a Checklist",
    color: "from-orange-500 to-amber-500",
    text: "Go to Checklists tab, tap 'Create'. Name it (e.g., 'My Morning Routine'), pick a category, then search and add 3–7 products from our 8,500+ database. Free users get 5, Glow Badge 10, Glow Pro 20."
  },
  {
    num: "3",
    icon: ImagePlus,
    title: "Share a Post",
    color: "from-emerald-500 to-teal-500",
    text: "Tap '+', upload photos (up to 5), write a caption, and attach your checklist. Every post on Gloverse shows exactly what products you used."
  },
  {
    num: "4",
    icon: Sparkles,
    title: "Create a Look",
    color: "from-violet-500 to-purple-500",
    text: "Go to Spotlight tab, tap '+'. Pick a theme (Summer, Wedding, Night Out...), upload photos, attach a checklist. Looks get discovered by everyone — not just your followers."
  },
  {
    num: "5",
    icon: PackagePlus,
    title: "Add Missing Products",
    color: "from-blue-500 to-cyan-500",
    text: "Can't find a product? Go to Settings > 'Suggest a Product'. Fill in the name, brand, price and purchase link. We'll verify and add it for the whole community."
  },
];

const PERKS = [
  { icon: ShoppingBag, title: "Shoppable Content", desc: "Every post has a product checklist — followers buy what you use in one tap" },
  { icon: Users, title: "Get Discovered", desc: "Looks appear on Spotlight by theme, reaching new audiences beyond your followers" },
  { icon: Star, title: "Glow Badge", desc: "Get verified as a creator — your beauty version of the blue tick" },
  { icon: BarChart3, title: "Creator Dashboard", desc: "Track product clicks, saves, and top-performing checklists" },
  { icon: TrendingUp, title: "Earn from Your Influence", desc: "Product clicks are tracked to you — built for future affiliate revenue" },
];

export default function QuickStartPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="quickstart-page">
      <style>{`@media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      {/* Header */}
      <div className="no-print sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="quickstart-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button onClick={() => window.print()} className="px-4 py-2 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800" data-testid="quickstart-print-btn">
            Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <img src="/gloverse-logo.png" alt="Gloverse" className="w-40 mx-auto mb-3" style={{ filter: 'brightness(0.2)' }} />
          <h1 className="text-2xl font-black text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Get Started in 5 Steps
          </h1>
          <p className="text-sm text-gray-500">Everything you need to know — in under 2 minutes.</p>
        </div>

        {/* Steps */}
        <div className="space-y-4 mb-10">
          {STEPS.map((step) => (
            <div key={step.num} className="flex gap-4 items-start" data-testid={`quickstart-step-${step.num}`}>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shrink-0 shadow-sm`}>
                <step.icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{step.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 uppercase font-semibold tracking-wider">Why Gloverse</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Perks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {PERKS.map((perk, i) => (
            <div key={i} className="flex gap-3 items-start p-3.5 rounded-xl bg-gray-50 border border-gray-100" data-testid={`quickstart-perk-${i}`}>
              <perk.icon className="w-5 h-5 text-pink-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900">{perk.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{perk.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center pb-8">
          <p className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to glow?
          </p>
          <p className="text-sm text-gray-500 mb-4">Sign up, create your first checklist, and share your beauty journey.</p>
          <button
            onClick={() => navigate('/login')}
            className="no-print px-8 py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-orange-500 transition-all shadow-md"
            data-testid="quickstart-signup-btn"
          >
            Join Gloverse
          </button>
          <p className="text-xs text-gray-400 mt-6">Questions? Reach out at info@gloverse.in</p>
        </div>
      </div>
    </div>
  );
}
